import { createContext } from '@lit/context'

/**
 * The state a [Field](/components/field/) shares with the form control it wraps.
 *
 * This mirrors Base UI React's `FieldRootContext`, trimmed to what Nord needs
 * today. The Field is the provider; every control consumes it through
 * `FieldControlMixin`. When a control resolves a value (i.e. it is inside a
 * Field), it suppresses its own built-in label/hint/error and lets the Field
 * parts take over.
 *
 * Validation/state-tracking fields (touched, dirty, filled, validity, …) are
 * intentionally deferred — this is the seam where they would be added.
 */
export interface FieldContextValue {
  /** Field-level invalid state. The control ORs this with its own. */
  invalid: boolean
  /** Field-level disabled (incl. any fieldset cascade). The control ORs this with its own. */
  disabled: boolean
  /**
   * The `id` of the `<nord-field-label>`, for light-DOM controls to reference
   * via `aria-labelledby` (IDREFs resolve in the same tree).
   */
  labelId: string | undefined
  /**
   * The label's text content, for shadow-DOM controls to use as `aria-label`
   * (an external `aria-labelledby` cannot cross the shadow boundary).
   */
  labelText: string | undefined
  /**
   * Space-joined ids of the field's description(s) and error(s), in DOM order,
   * for the control's `aria-describedby`.
   */
  describedById: string | undefined
  /**
   * The actual helper elements for Shadow DOM controls. Modern browsers can
   * reflect these across the shadow boundary with `ariaDescribedByElements`.
   */
  describedByElements: readonly HTMLElement[]
  /**
   * Lets the control publish its own `required` up to the Field so the
   * `<nord-field-label>` can render the required indicator. The control owns
   * `required`; the label only reflects it.
   */
  setRequired: (required: boolean) => void
}

/**
 * Context shared by `<nord-field>` (provider) with its control (consumer).
 * `undefined` means "not inside a Field" — the control renders its own
 * built-in field UI.
 */
export const fieldContext = createContext<FieldContextValue | undefined>(
  Symbol('nord-field-context'),
)
